import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PrivacyPolicy = () => {
  const [customContent, setCustomContent] = useState(null);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/settings`);
        if (res.data.privacy_policy_content) {
          setCustomContent(res.data.privacy_policy_content);
        }
      } catch (e) {
        console.error('Failed to fetch settings:', e);
      }
    };
    fetchSettings();
  }, []);

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Navbar />
      
      <div className="pt-44 pb-20">
        <div className="max-w-4xl mx-auto px-6">
          <h1 className="text-4xl font-bold text-[#0a0203] mb-8">Privacy Policy</h1>
          
          <div className="bg-white rounded-2xl shadow-sm p-8 md:p-12 prose prose-lg max-w-none">
            <p className="text-gray-500 mb-8">Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
            
            {customContent ? (
              <div className="text-gray-600" dangerouslySetInnerHTML={{ __html: customContent }} />
            ) : (
              <>
            <h2 className="text-2xl font-semibold text-[#0a0203] mt-8 mb-4">1. Scope and Applicability</h2>
            <p className="text-gray-600 mb-6">
              This Privacy Policy applies to all customers and visitors in <strong>Canada</strong> and the <strong>United States of America</strong>. 
              LyphLine Freeze Dryers, operated by Peak Prairie Enterprises, is committed to protecting the privacy of individuals in both countries 
              in accordance with applicable privacy laws, including Canada's Personal Information Protection and Electronic Documents Act (PIPEDA), 
              provincial privacy legislation, and US federal and state privacy laws.
            </p>

            <h2 className="text-2xl font-semibold text-[#0a0203] mt-8 mb-4">2. Information We Collect</h2>
            <p className="text-gray-600 mb-4">
              We collect information you provide directly to us, such as when you create an account, make a purchase, 
              sign up for our newsletter, or contact us for support. This information may include:
            </p>
            <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
              <li>Name and contact information (email address, phone number, mailing address)</li>
              <li>Payment information (credit card numbers, billing address)</li>
              <li>Order history and preferences</li>
              <li>Communications with our customer service team</li>
              <li>Location data (country, province/state) to provide appropriate services and pricing</li>
            </ul>

            <h2 className="text-2xl font-semibold text-[#0a0203] mt-8 mb-4">3. How We Use Your Information</h2>
            <p className="text-gray-600 mb-4">We use the information we collect to:</p>
            <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
              <li>Process and fulfill your orders (shipping within Canada and the United States)</li>
              <li>Send you order confirmations and shipping updates</li>
              <li>Respond to your comments, questions, and customer service requests</li>
              <li>Send you marketing communications (with your consent)</li>
              <li>Improve our website and products</li>
              <li>Provide country-specific pricing and financing options</li>
            </ul>

            <h2 className="text-2xl font-semibold text-[#0a0203] mt-8 mb-4">4. Information Sharing</h2>
            <p className="text-gray-600 mb-6">
              We do not sell, trade, or otherwise transfer your personal information to third parties except 
              as necessary to fulfill orders (e.g., shipping carriers in Canada and the US, payment processors) or as required by law 
              in either Canada or the United States.
            </p>

            <h2 className="text-2xl font-semibold text-[#0a0203] mt-8 mb-4">5. Cross-Border Data Transfers</h2>
            <p className="text-gray-600 mb-6">
              As we operate in both Canada and the United States, your personal information may be transferred to, 
              stored, and processed in either country. By using our services, you consent to the transfer of your 
              information between Canada and the United States. We ensure that appropriate safeguards are in place 
              to protect your information regardless of where it is processed.
            </p>

            <h2 className="text-2xl font-semibold text-[#0a0203] mt-8 mb-4">6. Data Security</h2>
            <p className="text-gray-600 mb-6">
              We implement appropriate security measures to protect your personal information against unauthorized 
              access, alteration, disclosure, or destruction. All payment transactions are encrypted using SSL technology, 
              compliant with both Canadian and US security standards.
            </p>

            <h2 className="text-2xl font-semibold text-[#0a0203] mt-8 mb-4">7. Cookies</h2>
            <p className="text-gray-600 mb-6">
              We use cookies to enhance your browsing experience, analyze site traffic, and personalize content. 
              You can choose to disable cookies through your browser settings. We comply with cookie consent 
              requirements in both Canada and applicable US jurisdictions.
            </p>

            <h2 className="text-2xl font-semibold text-[#0a0203] mt-8 mb-4">8. Your Rights</h2>
            <p className="text-gray-600 mb-4">
              Depending on your location, you may have specific rights regarding your personal information:
            </p>
            <p className="text-gray-600 mb-2"><strong>For Canadian Residents:</strong></p>
            <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-1">
              <li>Right to access your personal information</li>
              <li>Right to request correction of inaccurate information</li>
              <li>Right to withdraw consent for certain processing activities</li>
            </ul>
            <p className="text-gray-600 mb-2"><strong>For US Residents:</strong></p>
            <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-1">
              <li>Right to know what personal information we collect</li>
              <li>Right to request deletion of your personal information</li>
              <li>Right to opt-out of the sale of personal information (we do not sell your data)</li>
              <li>Additional rights may apply based on your state of residence</li>
            </ul>
            <p className="text-gray-600 mb-6">
              To exercise these rights, please contact us at <a href="mailto:service@lyphlinefreezedry.com" className="text-[#003456] hover:underline">service@lyphlinefreezedry.com</a>.
            </p>

            <h2 className="text-2xl font-semibold text-[#0a0203] mt-8 mb-4">9. Contact Us</h2>
            <p className="text-gray-600">
              If you have any questions about this Privacy Policy, please contact us at:
            </p>
            <p className="text-gray-600 mt-2">
              <strong>Email:</strong> <a href="mailto:service@lyphlinefreezedry.com" className="text-[#003456] hover:underline">service@lyphlinefreezedry.com</a><br />
              <strong>Canada:</strong> Peak Prairie Enterprises, Canada<br />
              <strong>United States:</strong> LyphLine Freeze Dryers operations serving US customers
            </p>
              </>
            )}
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
